/**
 * Modern Registration Form Component for THPT App
 * Features: Multi-step validation, password strength indicator,
 * Google SSO, smooth animations
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User, Phone, Loader2, Check, X } from 'lucide-react';

import { Button } from '@vsat/shared/components';
import { Input } from '@vsat/shared/components';
import { Checkbox } from '@vsat/shared/components';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@vsat/shared/components';
import { registerSchema, type RegisterFormData } from '@vsat/shared/lib/auth/schemas';
import { GoogleIcon, FacebookIcon } from './icons';
import { useAuthStore } from '@vsat/shared/store/authStore';
import { useNavigate } from 'react-router-dom';

interface RegisterFormProps {
  onSwitchToLogin?: () => void;
  onSuccess?: (data: RegisterFormData) => void;
  onNeedsVerification?: (email: string) => void;
}

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

export function RegisterForm({
  onSwitchToLogin,
  onSuccess,
  onNeedsVerification,
}: RegisterFormProps) {
  const navigate = useNavigate();
  const register = useAuthStore(state => state.register);
  const registerWithGoogle = useAuthStore(state => state.registerWithGoogle);
  const registerWithFacebook = useAuthStore(state => state.registerWithFacebook);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isFacebookLoading, setIsFacebookLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showLoginSuggestion, setShowLoginSuggestion] = useState(false);

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false,
    },
  });

  // Clear error message when user starts typing
  useEffect(() => {
    const subscription = form.watch((_value, { name }) => {
      if (name && errorMessage) {
        setErrorMessage('');
      }
    });
    return () => subscription.unsubscribe();
  }, [form, errorMessage]);

  const password = form.watch('password');

  const getPasswordStrength = (password: string): PasswordStrength => {
    if (!password) {
      return { score: 0, label: '', color: 'bg-slate-200' };
    }

    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) {
      return {
        score,
        label: 'Yếu',
        color: 'bg-red-500',
      };
    } else if (score === 3) {
      return {
        score,
        label: 'Trung bình',
        color: 'bg-yellow-500',
      };
    } else {
      return {
        score,
        label: 'Mạnh',
        color: 'bg-green-500',
      };
    }
  };

  const passwordStrength = getPasswordStrength(password);

  const passwordRequirements = [
    { met: password.length >= 8, label: 'Ít nhất 8 ký tự' },
    { met: /[A-Z]/.test(password), label: 'Có chữ hoa' },
    { met: /[a-z]/.test(password), label: 'Có chữ thường' },
    { met: /[0-9]/.test(password), label: 'Có số' },
    { met: /[@$!%*?&]/.test(password), label: 'Có ký tự đặc biệt' },
  ];

  const handleSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setErrorMessage('');
    setShowLoginSuggestion(false);

    try {
      await register({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone?.replace(/\s+/g, '') || '',
        password: data.password,
      });

      toast.success('Đăng ký thành công!', {
        description: 'Vui lòng kiểm tra email để xác thực tài khoản',
      });
      onSuccess?.(data);
    } catch (error: unknown) {
      console.error('Registration error:', error);

      let errorMsg = '';
      if (error && typeof error === 'object') {
        if ('response' in error && error.response && typeof error.response === 'object') {
          const response = error.response as { data?: { message?: string } };
          errorMsg = response.data?.message || '';
        }
        if (!errorMsg && 'message' in error) {
          errorMsg = (error as { message: string }).message;
        }
      }

      if (errorMsg === 'VERIFICATION_REQUIRED') {
        toast.success('Vui lòng xác thực email', {
          description: 'Chúng tôi đã gửi email xác thực đến địa chỉ email của bạn',
        });
        onNeedsVerification?.(data.email);
      } else {
        if (
          errorMsg.includes('already exists') ||
          errorMsg.includes('email or phone already exists') ||
          errorMsg.includes('User with this email or phone already exists')
        ) {
          setErrorMessage('Email hoặc số điện thoại đã được sử dụng');
          setShowLoginSuggestion(true);
        } else if (errorMsg.includes('Invalid phone')) {
          setErrorMessage('Số điện thoại không hợp lệ');
        } else if (
          errorMsg.includes('Network') ||
          errorMsg.includes('Failed to fetch') ||
          (error &&
            typeof error === 'object' &&
            'code' in error &&
            (error as { code: string }).code === 'NETWORK_ERROR')
        ) {
          setErrorMessage('Lỗi kết nối mạng. Vui lòng thử lại sau.');
        } else {
          setErrorMessage('Đăng ký thất bại. Vui lòng thử lại.');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    setIsGoogleLoading(true);
    try {
      toast.info('Đang kết nối với Google...');
      await registerWithGoogle();
      toast.success('Đăng ký bằng Google thành công!');
      navigate('/assessment-history', { replace: true });
    } catch (error) {
      toast.error('Đăng ký bằng Google thất bại');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleFacebookRegister = async () => {
    setIsFacebookLoading(true);
    try {
      toast.info('Đang kết nối với Facebook...');
      await registerWithFacebook();
      toast.success('Đăng ký bằng Facebook thành công!');
      navigate('/assessment-history', { replace: true });
    } catch (error) {
      toast.error('Đăng ký bằng Facebook thất bại');
    } finally {
      setIsFacebookLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className='w-full max-w-md space-y-6'
    >
      {/* Social SSO Buttons */}
      <div className='grid grid-cols-2 gap-3'>
        <Button
          type='button'
          variant='outline'
          className='w-full relative group'
          onClick={handleGoogleRegister}
          disabled={isGoogleLoading || isLoading || isFacebookLoading}
        >
          {isGoogleLoading ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <GoogleIcon className='h-4 w-4' />
          )}
          <span className='ml-2'>Google</span>
        </Button>

        <Button
          type='button'
          variant='outline'
          className='w-full relative group'
          onClick={handleFacebookRegister}
          disabled={isFacebookLoading || isLoading || isGoogleLoading}
        >
          {isFacebookLoading ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <FacebookIcon className='h-4 w-4' />
          )}
          <span className='ml-2'>Facebook</span>
        </Button>
      </div>

      {/* Divider */}
      <div className='relative'>
        <div className='absolute inset-0 flex items-center'>
          <div className='w-full border-t border-border' />
        </div>
        <div className='relative flex justify-center text-xs uppercase'>
          <span className='bg-background px-2 text-muted-foreground'>hoặc tiếp tục với</span>
        </div>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className='rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4'>
          <div className='flex items-center'>
            <div className='flex-1'>
              <p className='text-sm font-medium text-red-800 dark:text-red-200'>{errorMessage}</p>
              {showLoginSuggestion && (
                <button
                  onClick={() => onSwitchToLogin?.()}
                  className='mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500 dark:hover:text-red-300 underline'
                >
                  Đăng nhập ngay
                </button>
              )}
            </div>
            <div className='ml-3 flex-shrink-0'>
              <button
                type='button'
                onClick={() => setErrorMessage('')}
                className='inline-flex rounded-md bg-red-50 dark:bg-red-900/20 p-1.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50 dark:focus:ring-offset-red-900/20'
              >
                <span className='sr-only'>Đóng</span>
                <X className='h-3 w-3' aria-hidden='true' />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registration Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-4'>
          {/* Name Fields */}
          <div className='grid grid-cols-2 gap-4'>
            <FormField
              control={form.control}
              name='firstName'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-sm font-medium text-foreground'>Tên</FormLabel>
                  <FormControl>
                    <div className='relative'>
                      <User className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                      <Input
                        placeholder='Văn A'
                        className='pl-10 border-border focus:border-primary focus:ring-2 focus:ring-primary/20'
                        {...field}
                        disabled={isLoading}
                        autoComplete='given-name'
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='lastName'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-sm font-medium text-foreground'>Họ</FormLabel>
                  <FormControl>
                    <Input
                      placeholder='Nguyễn'
                      className='border-border focus:border-primary focus:ring-2 focus:ring-primary/20'
                      {...field}
                      disabled={isLoading}
                      autoComplete='family-name'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Email Field */}
          <FormField
            control={form.control}
            name='email'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-sm font-medium text-foreground'>Email</FormLabel>
                <FormControl>
                  <div className='relative'>
                    <Mail className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                    <Input
                      type='email'
                      placeholder='email@example.com'
                      className='pl-10 border-border focus:border-primary focus:ring-2 focus:ring-primary/20'
                      {...field}
                      disabled={isLoading}
                      autoComplete='email'
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Phone Field */}
          <FormField
            control={form.control}
            name='phone'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-sm font-medium text-foreground'>Số điện thoại</FormLabel>
                <FormControl>
                  <div className='relative'>
                    <Phone className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                    <Input
                      type='tel'
                      placeholder='0987654321'
                      className='pl-10 border-border focus:border-primary focus:ring-2 focus:ring-primary/20'
                      {...field}
                      disabled={isLoading}
                      autoComplete='tel'
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Password Field */}
          <FormField
            control={form.control}
            name='password'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-sm font-medium text-foreground'>Mật khẩu</FormLabel>
                <FormControl>
                  <div className='relative'>
                    <Lock className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder='••••••••'
                      className='pl-10 pr-10 border-border focus:border-primary focus:ring-2 focus:ring-primary/20'
                      {...field}
                      disabled={isLoading}
                      autoComplete='new-password'
                    />
                    <button
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors'
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                    </button>
                  </div>
                </FormControl>

                {/* Password Strength Indicator */}
                {password && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className='space-y-2'
                  >
                    <div className='flex items-center gap-2'>
                      <div className='flex-1 h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden'>
                        <motion.div
                          className={`h-full ${passwordStrength.color}`}
                          initial={{ width: 0 }}
                          animate={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                      <span className='text-xs font-medium text-slate-600 dark:text-muted-foreground'>
                        {passwordStrength.label}
                      </span>
                    </div>

                    {/* Requirements Checklist */}
                    <div className='space-y-1'>
                      {passwordRequirements.map((req, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className='flex items-center gap-2 text-xs'
                        >
                          {req.met ? (
                            <Check className='h-3 w-3 text-green-600' />
                          ) : (
                            <X className='h-3 w-3 text-muted-foreground' />
                          )}
                          <span
                            className={
                              req.met
                                ? 'text-green-600'
                                : 'text-slate-500 dark:text-muted-foreground'
                            }
                          >
                            {req.label}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                <FormMessage />
              </FormItem>
            )}
          />

          {/* Confirm Password Field */}
          <FormField
            control={form.control}
            name='confirmPassword'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-sm font-medium text-foreground'>
                  Xác nhận mật khẩu
                </FormLabel>
                <FormControl>
                  <div className='relative'>
                    <Lock className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder='••••••••'
                      className='pl-10 pr-10 border-border focus:border-primary focus:ring-2 focus:ring-primary/20'
                      {...field}
                      disabled={isLoading}
                      autoComplete='new-password'
                    />
                    <button
                      type='button'
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors'
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className='h-4 w-4' />
                      ) : (
                        <Eye className='h-4 w-4' />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Terms Acceptance */}
          <FormField
            control={form.control}
            name='acceptTerms'
            render={({ field }) => (
              <FormItem className='flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border p-4'>
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className='border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary'
                  />
                </FormControl>
                <div className='space-y-1 leading-none'>
                  <FormLabel className='text-sm font-normal text-foreground'>
                    Tôi đồng ý với{' '}
                    <a
                      href='/terms'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-primary hover:text-primary/80 font-medium'
                    >
                      Điều khoản sử dụng
                    </a>{' '}
                    và{' '}
                    <a
                      href='/privacy-policy'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-primary hover:text-primary/80 font-medium'
                    >
                      Chính sách bảo mật
                    </a>
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          {/* Submit Button */}
          <Button
            type='submit'
            className='w-full bg-primary hover:bg-primary/90 text-primary-foreground text-white'
            disabled={isLoading || isGoogleLoading || isFacebookLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Đang tạo tài khoản...
              </>
            ) : (
              'Đăng ký'
            )}
          </Button>
        </form>
      </Form>

      {/* Switch to Login */}
      <div className='text-center text-sm'>
        <span className='text-slate-600 dark:text-muted-foreground'>Đã có tài khoản? </span>
        <button
          onClick={onSwitchToLogin}
          className='font-medium text-primary hover:text-primary/80 transition-colors'
        >
          Đăng nhập ngay
        </button>
      </div>
    </motion.div>
  );
}
